// src/lib/adminApi.ts
// Admin API functions that use Supabase RPC to bypass RLS
// These functions call SECURITY DEFINER stored procedures
// that verify the admin session token before performing operations.

import { supabase, Listing } from './supabase';
import { getSessionToken } from '../contexts/AuthContext';

/**
 * Get the current admin session token.
 * Throws if no token is found.
 */
function getAdminToken(): string {
  const token = getSessionToken();
  if (!token) {
    throw new Error('No admin session token found. Please log in.');
  }
  return token;
}

/**
 * Fetch all pending listings (admin only).
 */
export async function adminGetPendingListings(): Promise<Listing[]> {
  const token = getAdminToken();

  const { data, error } = await supabase.rpc('admin_get_pending_listings', {
    session_token: token,
  });

  if (error) {
    console.error('Error fetching pending listings:', error);
    throw error;
  }

  return (data as Listing[]) || [];
}

/**
 * Fetch all approved listings (admin only).
 */
export async function adminGetApprovedListings(): Promise<Listing[]> {
  const token = getAdminToken();

  const { data, error } = await supabase.rpc('admin_get_approved_listings', {
    session_token: token,
  });

  if (error) {
    console.error('Error fetching approved listings:', error);
    throw error;
  }

  return (data as Listing[]) || [];
}

/**
 * Approve a pending listing (admin only).
 */
export async function adminApproveListing(listingId: string): Promise<void> {
  const token = getAdminToken();

  const { error } = await supabase.rpc('admin_approve_listing', {
    session_token: token,
    listing_id: listingId,
  });

  if (error) {
    console.error('Error approving listing:', error);
    throw error;
  }
}

/**
 * Reject (delete) a pending listing (admin only).
 */
export async function adminRejectListing(listingId: string): Promise<void> {
  const token = getAdminToken();

  const { error } = await supabase.rpc('admin_reject_listing', {
    session_token: token,
    listing_id: listingId,
  });

  if (error) {
    console.error('Error rejecting listing:', error);
    throw error;
  }
}

/**
 * Update a listing's fields (admin only).
 */
export async function adminUpdateListing(
  listingId: string,
  updates: Partial<Listing>
): Promise<void> {
  const token = getAdminToken();

  const { error } = await supabase.rpc('admin_update_listing', {
    session_token: token,
    listing_id: listingId,
    p_brand: updates.brand ?? null,
    p_model: updates.model ?? null,
    p_price: updates.price ?? null,
    p_year: updates.year ?? null,
    p_mileage: updates.mileage ?? null,
    p_fuel_type: updates.fuel_type ?? null,
    p_transmission: updates.transmission ?? null,
    p_color: updates.color ?? null,
    p_location: updates.location ?? null,
    p_condition: updates.condition ?? null,
    p_description: updates.description ?? null,
    p_contact_phone: updates.contact_phone ?? null,
    p_contact_email: updates.contact_email ?? null,
  });

  if (error) {
    console.error('Error updating listing:', error);
    throw error;
  }
}

/**
 * Delete an approved listing (admin only).
 */
export async function adminDeleteListing(listingId: string): Promise<void> {
  const token = getAdminToken();

  const { error } = await supabase.rpc('admin_delete_listing', {
    session_token: token,
    listing_id: listingId,
  });

  if (error) {
    console.error('Error deleting listing:', error);
    throw error;
  }
}
